import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { serverUrl } from '../platform/server.js'
import { documentLabel, documentDownloadName } from '../lib/documentUpload.js'

export function fmtDur(sec) {
  if (!sec && sec !== 0) return ''
  const s = Math.round(sec)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

// 会话里的图片/语音。文件走鉴权接口，要先带令牌取回来换成 blob 地址，
// 所以这里自己管加载状态和 revoke，组件卸载时必须释放，否则翻会话会一直漏内存。
export function MediaBubble({ media, delivery, onViewImage }) {
  const [url, setUrl] = useState('')
  const [err, setErr] = useState('')

  useEffect(() => {
    let dead = false
    let created = ''
    setUrl(''); setErr('')
    api.mediaObjectUrl(media.file)
      .then(u => { if (dead) { URL.revokeObjectURL(u); return } created = u; setUrl(u) })
      .catch(e => { if (!dead) setErr(e.message) })
    return () => { dead = true; if (created) URL.revokeObjectURL(created) }
  }, [media.file])

  const pendingWarning = delivery === 'pending'
    ? <div className="small" role="status" style={{ color: '#b45309', marginTop: 6 }}>发送结果待确认，请勿重复发送</div> : null
  if (err) return <div>{pendingWarning}<span className="small muted">附件打不开：{err}</span></div>
  if (!url) return <div>{pendingWarning}<span className="small muted">加载中…</span></div>

  if (media.kind === 'voice') {
    return (
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <audio src={url} controls preload="metadata" style={{ height: 32, maxWidth: 240 }} />
        {media.duration ? <span className="small muted">{fmtDur(media.duration)}</span> : null}
      </div>
    )
  }
  if (media.kind === 'image') {
    return (
      <img src={url} alt="客户发来的图片" onClick={() => onViewImage?.(url)}
        title="点击查看大图"
        style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, display: 'block', cursor: 'zoom-in' }} />
    )
  }
  // 视频/文件等：不做预览，给个能存下来的入口就够了
  return media.kind === 'file'
    ? <div><DocumentCard filename={media.filename || media.file} size={media.size} href={url} download={media.filename || media.file} />
        {pendingWarning}</div>
    : <a href={url} download className="small">下载附件（{media.kind}）</a>
}

// 从素材库发出去的图片/视频。会话里只记了「［已发送图片：素材名］」这行回执，
// 按名字到素材库里找文件显示出来；素材后来被删或改名就找不到，只能退回显示文字。
// 回执格式与后端 mediaReceipt 一致：［已发送图片：X］/［已发送视频：X］，改那边要一起改
export function findSentMedia(m, mediaList) {
  if (m.role !== 'agent' || m.meta?.media) return null
  const title = String(m.content || '').match(/^［已发送(?:图片|视频|文件)：(.+)］$/)?.[1]
  return (title && mediaList.find(x => x.title === title)) || null
}

export function KbMediaBubble({ med, onViewImage }) {
  const src = serverUrl(`/kb-media/${med.filename}`)
  return (
    <div>
      {med.kind === 'file'
        ? <DocumentCard filename={documentDownloadName(med)} size={med.size_bytes} href={src} download={documentDownloadName(med)} />
        : med.kind === 'video'
        ? <video src={src} controls preload="metadata" style={{ maxWidth: 240, maxHeight: 200, borderRadius: 8, display: 'block' }} />
        : <img src={src} alt={med.title} onClick={() => onViewImage?.(src)} title="点击查看大图"
            style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, display: 'block', cursor: 'zoom-in' }} />}
      <div className="small muted" style={{ marginTop: 4 }}>素材：{med.title}</div>
    </div>
  )
}

// 大图浮层。刻意做成覆盖层而不是新开标签页——运营的视线不能离开当前会话。
export function ImageLightbox({ url, onClose }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out',
    }}>
      <img src={url} alt="" style={{ maxWidth: '92%', maxHeight: '92%', borderRadius: 6 }} />
      <div style={{ position: 'absolute', top: 16, right: 20, color: '#fff', fontSize: 13 }}>
        点击任意处或按 Esc 关闭
      </div>
    </div>
  )
}

// Shared document tile: no broken <img>, no third-party document viewer.
export function DocumentCard({ filename, size, href, download, compact = false }) {
  const label = documentLabel(filename)
  const content = <>
    <span style={{ color: label === 'PDF' ? '#b45309' : 'var(--brand)', fontWeight: 700, fontSize: compact ? 14 : 18 }}>{label}</span>
    {!compact && <span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{filename}</span>}
    {!compact && <span className="small muted">{size != null ? `${(size / 1024 / 1024).toFixed(2)} MB · ` : ''}{href ? '下载文件' : '待发送文件'}</span>}
  </>
  const style = { display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8,
    padding: compact ? 12 : 16, minHeight: compact ? 66 : 100, boxSizing: 'border-box',
    background: 'var(--hover)', borderRadius: 8, color: 'var(--text)', textDecoration: 'none', maxWidth: 280 }
  return href ? <a href={href} download={download || filename} style={style}>{content}</a>
    : <div style={style} aria-label={filename}>{content}</div>
}
